import { RestClient } from '@/api/rest-client'
import { CancelToken } from 'axios'
import { Referral } from '@/api/model/referral'
import { User } from '@/api/model/user'
import { cloneDeep } from 'lodash'
import { Branch } from '@/api/model/branch'
import { Contact } from '@/api/model/contact'
import { Project } from '@/api/model/project'
import { ReferralCount, ReferralStatus } from '@/api/types'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { Pipeline } from '@/api/model/pipeline'
import { StatusReason } from '@/api/model/status-reason'
import { ReferralStats } from '@/api/model/referral-stats'
import { Company } from '@/api/model/company'
import { Source } from '@/api/model/source'

export class ReferralClient extends RestClient<Referral> {
    collectionName = 'referrals'
    parseData = Referral.fromData

    async create(referral: Referral): Promise<Referral> {
        const request = cloneDeep<any>(referral)

        if (referral.project instanceof Project) {
            request.project = referral.project.resourceIdentifier
        }

        if (referral.newSource instanceof Source) {
            request.newSource = referral.newSource.resourceIdentifier
        }

        if (referral.assigneeProject instanceof Project) {
            request.assigneeProject = referral.assigneeProject.resourceIdentifier
        }

        if (referral.phase instanceof PipelinePhase) {
            request.phase = referral.phase.resourceIdentifier
        }

        if (referral.pipeline instanceof Pipeline) {
            request.pipeline = referral.pipeline.resourceIdentifier
        }

        return super.create(request)
    }

    async update(id: string, data: Referral): Promise<Referral> {
        const request = cloneDeep<any>(data)

        if (data.assignedTo instanceof User) {
            request.assignedTo = data.assignedTo.resourceIdentifier
        }

        if (data.newSource instanceof Source) {
            request.newSource = data.newSource.resourceIdentifier
        }

        if (data.assignedToBranch instanceof Branch) {
            request.assignedToBranch = data.assignedToBranch.resourceIdentifier
        }

        if (data.contact instanceof Contact) {
            request.contact = data.contact.resourceIdentifier
        }

        if (data.referredBy instanceof User) {
            request.referredBy = data.referredBy.resourceIdentifier
        }

        if (data.referredByBranch instanceof Branch) {
            request.referredByBranch = data.referredByBranch.resourceIdentifier
        }

        if (data.project instanceof Project) {
            request.project = data.project.resourceIdentifier
        }

        if (data.assigneeProject instanceof Project) {
            request.assigneeProject = data.assigneeProject.resourceIdentifier
        }

        if (data.phase instanceof PipelinePhase) {
            request.phase = data.phase.resourceIdentifier
        }

        if (data.pipeline instanceof Pipeline) {
            request.pipeline = data.pipeline.resourceIdentifier
        }

        if (data.newStatusReason instanceof StatusReason) {
            request.newStatusReason = data.newStatusReason.resourceIdentifier
        }

        if (data.orderValue !== null) {
            request.orderValue = Number(data.orderValue)
        }

        if (data.leadCommission !== null) {
            request.leadCommission = Number(data.leadCommission)
        }

        // Fix futureFrom sometimes being an empty string
        if (data.futureFrom as any as unknown === '') {
            request.futureFrom = null
        }

        return super.update(id, request)
    }

    async delete(id: string): Promise<void> {
        await this.client.put(`${this.basePath}/${this.collectionName}/${id}/delete`, {
            id,
        })
    }

    async updateScore(id: string, score: number): Promise<void> {
        try {
            await this.client.put(`${this.basePath}/${this.collectionName}/${id}/update_score`, {
                score,
            })

        } catch (error) {
            this.handleError(error)
        }
    }

    async updateManualValuationData(id: string, valuation: number, user: User): Promise<void> {
        try {
            await this.client.put(`${this.basePath}/${this.collectionName}/${id}/update_manual_valuation_data`, {
                valuation,
                user: user.resourceIdentifier,
            })

        } catch (error) {
            this.handleError(error)
        }
    }

    async updateReferredBy(company: Company, id: string, referredBy: User, changedBy: User): Promise<void> {
        try {
            await this.client.put(`${this.basePath}/${this.collectionName}/${id}/update_referred_by`, {
                company: company.resourceIdentifier,
                referredBy: referredBy.resourceIdentifier,
                changedBy: changedBy.resourceIdentifier,
            })
        } catch (error) {
            this.handleError(error)
        }
    }

    async updateHousetypeData(id: string, housetype: string): Promise<void> {
        try {
            await this.client.put(`${this.basePath}/${this.collectionName}/${id}/update_housetype_data`, {
                housetype,
            })

        } catch (error) {
            this.handleError(error)
        }
    }

    notify(id: string, type: string, targets: string[]): void {
        try {
            this.client.put(
                `${this.basePath}/${this.collectionName}/${id}/notify`,
                {type, targets},
                )
        } catch (error) {
            this.handleError(error)
        }
    }

    async sendNewBuildProjectReferralEmail(referralId: string, type: string): Promise<void> {
        try {
            await this.client.put(`${this.basePath}/${this.collectionName}/${referralId}/notify/send-new-build-project-referral-email`, {
                referralId,
                type,
            })

        } catch (error) {
            this.handleError(error)
        }
    }

    async previewProjectEmailTemplate(id: string, type: string): Promise<{ from_email: string, from_name: string, subject: string, html: string }> {
        try {
            const response = await this.client.put(`${this.basePath}/${this.collectionName}/${id}/project/preview-email-template`, {
                id,
                type,
            })

            return response.data

        } catch (error) {
            this.handleError(error)
        }
    }

    async updateExtendedAppraisal(id: string): Promise<void> {
        try {
            await this.client.put(`${this.basePath}/${this.collectionName}/${id}/update-extended-appraisal`, {
                id,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                },
            })

        } catch (error) {
            this.handleError(error)
        }
    }

    async getReferralCountForStatus(status: ReferralStatus, params: object, cancelToken: CancelToken = null): Promise<ReferralCount> {
        try {
            const response = await this.client.post(`${this.basePath}/${this.collectionName}/counts/list`, {
                status,
            }, {
                params: {
                    ...params,
                    status: null,
                },
                cancelToken,
            })
            return response.data

        } catch (error) {
            this.handleError(error)
        }
    }

    async getCounts(pipeline: string | null, phase: string | null, status: string | null, category: string | null, params: object, cancelToken: CancelToken = null): Promise<ReferralStats> {
        try {
            const response = await this.client.post(`${this.basePath}/${this.collectionName}/new_counts`, {
                pipeline,
                phase,
                status,
                category,
            }, {
                params: {
                    ...params,
                    pipeline: null,
                    phase: null,
                    status: null,
                    category: null,
                },
                cancelToken,
            })

            return ReferralStats.fromJson(response.data)
        } catch (error) {
            this.handleError(error)
        }
    }
}
